import type {
  AnalyticsAPI,
  AnalyticsEvents,
  GlobalProperties,
  InferUserEvents,
  UserIdentificationPayload,
} from './analytics.type';

type Mixpanel = (typeof import('mixpanel-browser'))['default'];

let mixpanel: Nullable<Mixpanel> = null;

const getMixpanel = async () => {
  const mixpanelModule = await import('mixpanel-browser');
  mixpanel = mixpanelModule.default;
  return mixpanel;
};

const handleEvent = <T extends AnalyticsEvents>(
  event: keyof T & string,
  payload?: Record<string, unknown>
) => {
  if (!mixpanel) return;
  mixpanel.track(event, payload);
};

const identifyUser = (payload: UserIdentificationPayload) => {
  if (!mixpanel) return;
  const { userId, ...properties } = payload;
  mixpanel.identify(userId);
  mixpanel.people.set(properties);
};

const registerGlobalProperties = (properties: GlobalProperties) => {
  if (!mixpanel) return;
  const { daysOrOptions, ...rest } = properties;
  mixpanel.register(rest, daysOrOptions);
};

const setGroup = (groupId: string, value: string | number) => {
  if (!mixpanel) return;
  mixpanel.set_group(groupId, value);
};

const getGroup = (groupId: string, value: string) => {
  if (!mixpanel) return;
  return mixpanel.get_group(groupId, value);
};

export const createMixPanelAPI = async <
  T extends AnalyticsEvents = InferUserEvents,
>(
  token: string,
  mixpanelApiHost: Maybe<string>,
  shouldDebug: boolean
): Promise<AnalyticsAPI> => {
  const loadedMixpanel = await getMixpanel();

  loadedMixpanel.init(token, {
    debug: shouldDebug,
    ignore_dnt: shouldDebug,
    api_host: mixpanelApiHost,
  });

  return {
    handleEvent: handleEvent<T>,
    identifyUser,
    registerGlobalProperties,
    setGroup,
    getGroup,
  };
};
